import { useMemo, useState } from 'react';
import { Check, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS,
  PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL,
  buildPeripheralMuscleStrengthPayload,
  convertForceValue,
  getDefaultPeripheralMuscleSelection,
  normalizePeripheralMuscleStrengthPayload,
  summarizePeripheralMuscleStrengthResult,
  type PeripheralForceUnit,
  type PeripheralMuscleStrengthKey,
  type PeripheralMuscleStrengthPatientContext,
  type PeripheralMuscleStrengthResultPayload,
  type PeripheralMuscleStrengthSide,
} from '../peripheral-muscle/peripheralMuscleDomain';
import { normalizePatientHeightCm, normalizePatientWeightKg } from '../utils/patientMetrics';

interface PeripheralMuscleStrengthTestProps {
  initial?: PeripheralMuscleStrengthResultPayload | null;
  patientContext?: PeripheralMuscleStrengthPatientContext;
  onCancel?: () => void;
  onNext: (result: PeripheralMuscleStrengthResultPayload) => void;
}

export type { PeripheralMuscleStrengthResultPayload };

type MuscleInputsState = Record<PeripheralMuscleStrengthKey, string[]>;

const parseInputValue = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const formatForceInput = (value: number | null, unit: PeripheralForceUnit): string => {
  if (value == null) return '';
  const decimals = unit === 'kgf' ? 1 : 0;
  return value.toFixed(decimals).replace('.', ',');
};

const ensureMinInputs = (values: string[]): string[] => {
  const next = [...values];
  while (next.length < PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts) {
    next.push('');
  }
  return next;
};

const buildInitialMuscleInputs = (
  initial: PeripheralMuscleStrengthResultPayload | null | undefined,
  unit: PeripheralForceUnit,
): MuscleInputsState => {
  const normalized = normalizePeripheralMuscleStrengthPayload(initial);

  return PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.reduce((accumulator, definition) => {
    const attemptsN = normalized?.muscles[definition.key]?.attemptsN ?? [];
    const attemptsInSelectedUnit = attemptsN.map((attemptN) =>
      formatForceInput(convertForceValue(attemptN, 'N', unit), unit),
    );
    accumulator[definition.key] = ensureMinInputs(attemptsInSelectedUnit);
    return accumulator;
  }, {} as MuscleInputsState);
};

const convertMuscleInputsUnit = (current: MuscleInputsState, from: PeripheralForceUnit, to: PeripheralForceUnit): MuscleInputsState =>
  PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.reduce((accumulator, definition) => {
    accumulator[definition.key] = current[definition.key].map((value) => {
      const parsed = parseInputValue(value);
      return formatForceInput(convertForceValue(parsed, from, to), to);
    });
    return accumulator;
  }, {} as MuscleInputsState);

const getMuscleAttemptValues = (inputs: string[], unit: PeripheralForceUnit): number[] =>
  inputs
    .map((value) => parseInputValue(value))
    .filter((value): value is number => value != null)
    .map((value) => convertForceValue(value, unit, unit) ?? value);

const sortSelectedMuscles = (selectedMuscles: PeripheralMuscleStrengthKey[]): PeripheralMuscleStrengthKey[] => {
  const defaultOrder = getDefaultPeripheralMuscleSelection();
  return [...selectedMuscles].sort((left, right) => defaultOrder.indexOf(left) - defaultOrder.indexOf(right));
};

function HelpPopover({
  label,
  lines,
  open,
  onToggle,
}: {
  label: string;
  lines: string[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-black text-slate-600 shadow-sm transition hover:border-sky-300 hover:text-sky-700"
        aria-label={`Como executar ${label}`}
      >
        ?
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-80 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-xl">
          <div className="text-sm font-bold text-slate-900">{label}</div>
          <div className="mt-3 space-y-2 text-xs leading-relaxed text-slate-600">
            {lines.map((line, index) => (
              <p key={`${label}-${index}`}>{line}</p>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MetricChip({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'good' | 'warn' | 'danger' }) {
  const toneClass =
    tone === 'good'
      ? 'border-slate-200 bg-white text-emerald-700'
      : tone === 'warn'
        ? 'border-slate-200 bg-white text-amber-700'
        : tone === 'danger'
          ? 'border-slate-200 bg-white text-rose-700'
          : 'border-slate-200 bg-white text-slate-700';

  return (
    <div className={`rounded-2xl border px-3 py-2 ${toneClass}`}>
      <div className="text-[10px] font-black uppercase tracking-[0.18em] opacity-70">{label}</div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

export default function PeripheralMuscleStrengthTest({
  initial = null,
  patientContext,
  onCancel,
  onNext,
}: PeripheralMuscleStrengthTestProps) {
  const normalizedInitial = normalizePeripheralMuscleStrengthPayload(initial);
  const initialUnit: PeripheralForceUnit = normalizedInitial?.unit === 'N' ? 'N' : 'kgf';
  const [unit, setUnit] = useState<PeripheralForceUnit>(initialUnit);
  const [dominantSide, setDominantSide] = useState<PeripheralMuscleStrengthSide>(
    normalizedInitial?.dominantSide === 'left' ? 'left' : 'right',
  );
  const [selectedMuscles, setSelectedMuscles] = useState<PeripheralMuscleStrengthKey[]>(
    normalizedInitial?.selectedMuscles?.length ? normalizedInitial.selectedMuscles : getDefaultPeripheralMuscleSelection(),
  );
  const [muscleInputs, setMuscleInputs] = useState<MuscleInputsState>(buildInitialMuscleInputs(normalizedInitial, initialUnit));
  const [notes, setNotes] = useState<string>(normalizedInitial?.notes ?? '');
  const [openHelpKey, setOpenHelpKey] = useState<PeripheralMuscleStrengthKey | null>(null);
  const [weightInput, setWeightInput] = useState<string>(
    normalizedInitial?.patientContext?.weightKg != null
      ? String(normalizedInitial.patientContext.weightKg).replace('.', ',')
      : patientContext?.weightKg != null
        ? String(normalizePatientWeightKg(patientContext.weightKg) ?? '').replace('.', ',')
        : '',
  );
  const [heightInput, setHeightInput] = useState<string>(
    normalizedInitial?.patientContext?.heightCm != null
      ? String(normalizedInitial.patientContext.heightCm).replace('.', ',')
      : patientContext?.heightCm != null
        ? String(normalizePatientHeightCm(patientContext.heightCm) ?? '').replace('.', ',')
        : '',
  );

  const normalizedWeightKg = normalizePatientWeightKg(parseInputValue(weightInput));
  const normalizedHeightCm = normalizePatientHeightCm(parseInputValue(heightInput));
  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      weightKg: normalizedWeightKg,
      heightCm: normalizedHeightCm,
    }),
    [normalizedHeightCm, normalizedWeightKg, patientContext],
  );

  const attemptsByMuscle = useMemo(
    () =>
      PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.reduce((accumulator, definition) => {
        accumulator[definition.key] = getMuscleAttemptValues(muscleInputs[definition.key], unit);
        return accumulator;
      }, {} as Partial<Record<PeripheralMuscleStrengthKey, number[]>>),
    [muscleInputs, unit],
  );

  const previewPayload = useMemo(
    () =>
      buildPeripheralMuscleStrengthPayload({
        unit,
        dominantSide,
        selectedMuscles,
        attemptsByMuscle,
        notes,
        patientContext: effectivePatientContext,
      }),
    [attemptsByMuscle, dominantSide, effectivePatientContext, notes, selectedMuscles, unit],
  );

  const summary = useMemo(() => summarizePeripheralMuscleStrengthResult(previewPayload), [previewPayload]);
  const musclesBelowRecommendation = summary.muscles
    .filter((muscle) => !muscle.completed || !muscle.reproducible)
    .map((muscle) => muscle.label);
  const hasSelectedMuscles = summary.selectedMusclesCount > 0;
  const hasAnyMeasurement = summary.muscles.some((muscle) => muscle.attemptsN.length > 0);
  const canFinish = hasSelectedMuscles && hasAnyMeasurement;

  const handleUnitChange = (nextUnit: PeripheralForceUnit) => {
    if (nextUnit === unit) return;
    setMuscleInputs((current) => convertMuscleInputsUnit(current, unit, nextUnit));
    setUnit(nextUnit);
  };

  const toggleMuscleSelection = (muscleKey: PeripheralMuscleStrengthKey) => {
    setSelectedMuscles((current) => {
      const next = current.includes(muscleKey) ? current.filter((key) => key !== muscleKey) : [...current, muscleKey];
      return sortSelectedMuscles(next);
    });

    setOpenHelpKey((current) => (current === muscleKey ? null : current));
  };

  const updateAttemptInput = (muscleKey: PeripheralMuscleStrengthKey, index: number, value: string) => {
    setMuscleInputs((current) => {
      const next = { ...current };
      next[muscleKey] = [...current[muscleKey]];
      next[muscleKey][index] = value;
      return next;
    });
  };

  const addAttemptInput = (muscleKey: PeripheralMuscleStrengthKey) => {
    setMuscleInputs((current) => {
      const currentInputs = current[muscleKey];
      if (currentInputs.length >= PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.maxAttempts) return current;
      return {
        ...current,
        [muscleKey]: [...currentInputs, ''],
      };
    });
  };

  const removeAttemptInput = (muscleKey: PeripheralMuscleStrengthKey, index: number) => {
    setMuscleInputs((current) => {
      const nextInputs = current[muscleKey].filter((_, currentIndex) => currentIndex !== index);
      return {
        ...current,
        [muscleKey]: ensureMinInputs(nextInputs),
      };
    });
  };

  const clearMuscleInputs = (muscleKey: PeripheralMuscleStrengthKey) => {
    setMuscleInputs((current) => ({
      ...current,
      [muscleKey]: ensureMinInputs([]),
    }));
  };

  const handleFinish = () => {
    if (!hasSelectedMuscles) {
      toast.error('Selecione ao menos 1 grupo muscular para concluir a avaliação.');
      return;
    }

    if (!hasAnyMeasurement) {
      toast.error('Registre ao menos uma medida válida para concluir a avaliação.');
      return;
    }

    if (musclesBelowRecommendation.length > 0) {
      toast.warning(
        `Avaliação salva com ressalva: recomenda-se registrar ao menos ${PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts} medidas por grupo muscular e confirmar a reprodutibilidade entre os 2 maiores valores.`,
      );
    }

    onNext({
      ...previewPayload,
      finishedAt: new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-2xl border border-primary-100 bg-white p-4 text-slate-700 shadow-sm">
        <div className="rounded-xl bg-primary-50 p-2 text-primary-700">
          <Info className="h-5 w-5" />
        </div>
        <div>
          <p className="font-semibold text-slate-900">Força muscular periférica - dinamometria manual</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            Recomenda-se registrar ao menos {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts} medidas por músculo. A reprodutibilidade é
            verificada pelos 2 maiores valores; dados parciais também podem ser salvos com a devida ressalva.
          </p>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Escopo da avaliação</p>
            <h3 className="mt-1 text-lg font-black text-slate-900">Grupos musculares selecionados</h3>
            <p className="mt-2 text-sm text-slate-600">Escolha apenas os grupos musculares que deseja avaliar nesta sessão.</p>
          </div>
          <div className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500">
            {summary.selectedMusclesCount} selecionado(s)
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.map((definition) => {
            const checked = selectedMuscles.includes(definition.key);

            return (
              <label
                key={`selector-${definition.key}`}
                className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                  checked
                    ? 'border-primary-300 bg-primary-50/70 text-slate-900 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-primary-200 hover:bg-primary-50/30'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 accent-primary-600"
                  checked={checked}
                  onChange={() => toggleMuscleSelection(definition.key)}
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{definition.shortLabel}</div>
                  <div className={`mt-1 text-xs ${checked ? 'text-primary-700' : 'text-slate-500'}`}>{definition.equationLabel}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {!hasSelectedMuscles ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Selecione ao menos 1 grupo muscular para habilitar o registro das tentativas.
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Contexto do paciente</p>
              <h3 className="mt-1 text-lg font-black text-slate-900">Variáveis para as equações do suplemento</h3>
            </div>
            <div className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500">
              {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.supplier}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Sexo</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">
                {String(patientContext?.sex || '').toUpperCase().startsWith('F') ? 'Feminino' : 'Masculino'}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Idade</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">
                {patientContext?.ageYears != null ? `${patientContext.ageYears} anos` : 'Sem idade cadastrada'}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Lado avaliado</div>
              <select
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                value={dominantSide}
                onChange={(event) => setDominantSide(event.target.value as PeripheralMuscleStrengthSide)}
              >
                <option value="right">Direito</option>
                <option value="left">Esquerdo</option>
              </select>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-sm">
              Peso usado nas equações (kg)
              <input
                type="number"
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                value={weightInput}
                onChange={(event) => setWeightInput(event.target.value)}
                placeholder="Ex.: 72,5"
              />
            </label>
            <label className="text-sm">
              Altura usada nas equações (cm)
              <input
                type="number"
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                value={heightInput}
                onChange={(event) => setHeightInput(event.target.value)}
                placeholder="Ex.: 168"
              />
            </label>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">BMI</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">
                {summary.bmi != null ? `${summary.bmi.toFixed(2)} kg/m²` : 'Preencha peso e altura'}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm font-medium text-slate-700">Unidade de entrada</div>
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
              {(['kgf', 'N'] as const).map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  onClick={() => handleUnitChange(candidate)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                    unit === candidate ? 'bg-primary-700 text-white shadow-sm' : 'text-slate-600 hover:text-primary-700'
                  }`}
                >
                  {candidate}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              As equações são calculadas em Newtons; a interface converte automaticamente quando você trabalha em kgf.
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
          <MetricChip
            label="Músculos concluídos"
            value={hasSelectedMuscles ? `${summary.completedMuscles}/${summary.selectedMusclesCount}` : 'Nenhum'}
            tone={summary.allMusclesCompleted ? 'good' : 'warn'}
          />
          <MetricChip
            label="Reprodutíveis"
            value={hasSelectedMuscles ? `${summary.reproducibleMuscles}/${summary.selectedMusclesCount}` : 'Nenhum'}
            tone={summary.allMusclesReproducible ? 'good' : 'warn'}
          />
          <MetricChip
            label="Média % predito"
            value={summary.averagePercentPredicted != null ? `${summary.averagePercentPredicted.toFixed(1)}%` : 'Sem referência'}
            tone="default"
          />
          <MetricChip
            label="Menor % predito"
            value={
              summary.lowestPercentPredicted != null && summary.lowestMuscleLabel
                ? `${summary.lowestMuscleLabel}: ${summary.lowestPercentPredicted.toFixed(1)}%`
                : 'Sem referência'
            }
            tone="default"
          />
        </div>
      </div>

      {hasSelectedMuscles && musclesBelowRecommendation.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Recomenda-se registrar ao menos {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts} medidas e confirmar a reprodutibilidade entre
          os 2 maiores valores para: {musclesBelowRecommendation.join(', ')}. Isso não impede o salvamento da avaliação.
        </div>
      ) : null}

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Execução</p>
            <h3 className="mt-1 text-lg font-black text-slate-900">Tentativas por grupo muscular</h3>
          </div>
          <div className="text-xs text-slate-500">
            Recomendado: ao menos {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts} medidas • máximo de{' '}
            {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.maxAttempts}
          </div>
        </div>

        {!hasSelectedMuscles ? (
          <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
            Nenhum grupo muscular selecionado.
          </div>
        ) : (
          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            {summary.muscles.map((muscle) => {
              const isHelpOpen = openHelpKey === muscle.key;
              const currentInputs = muscleInputs[muscle.key];
              const diffTone =
                muscle.topTwoDiffPercent == null
                  ? 'default'
                  : muscle.reproducible
                    ? 'good'
                    : 'warn';

              return (
                <div key={muscle.key} className="rounded-[28px] border border-slate-200 bg-slate-50 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Grupo muscular</div>
                      <h4 className="mt-1 text-lg font-black text-slate-900">{muscle.label}</h4>
                      <p className="mt-1 text-xs text-slate-500">Equação: {muscle.equationLabel}</p>
                    </div>
                    <HelpPopover
                      label={muscle.label}
                      lines={muscle.helpDescription}
                      open={isHelpOpen}
                      onToggle={() => setOpenHelpKey((current) => (current === muscle.key ? null : muscle.key))}
                    />
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <MetricChip
                      label="Melhor"
                      value={
                        muscle.bestKgf != null
                          ? unit === 'N'
                            ? `${(muscle.bestN ?? 0).toFixed(1)} N`
                            : `${muscle.bestKgf.toFixed(1)} kgf`
                          : '—'
                      }
                    />
                    <MetricChip
                      label="Predito"
                      value={
                        muscle.predictedKgf != null
                          ? unit === 'N'
                            ? `${(muscle.predictedN ?? 0).toFixed(1)} N`
                            : `${muscle.predictedKgf.toFixed(1)} kgf`
                          : 'Sem fórmula'
                      }
                    />
                    <MetricChip
                      label="% predito"
                      value={muscle.percentPredicted != null ? `${muscle.percentPredicted.toFixed(1)}%` : 'Sem fórmula'}
                      tone={
                        muscle.percentPredicted == null
                          ? 'default'
                          : muscle.percentPredicted < 80
                            ? 'danger'
                            : muscle.percentPredicted < 100
                              ? 'warn'
                              : 'good'
                      }
                    />
                    <MetricChip
                      label="Top 2"
                      value={
                        muscle.topTwoDiffPercent != null
                          ? `${muscle.topTwoDiffPercent.toFixed(1)}%`
                          : 'Aguardando 2 valores'
                      }
                      tone={diffTone}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-800">Tentativas ({unit})</div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-sm text-red-600 transition hover:text-red-700"
                        onClick={() => clearMuscleInputs(muscle.key)}
                      >
                        Limpar
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
                        onClick={() => addAttemptInput(muscle.key)}
                        disabled={currentInputs.length >= PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.maxAttempts}
                      >
                        <Plus className="h-4 w-4" />
                        Adicionar
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {currentInputs.map((value, index) => (
                      <label key={`${muscle.key}-${index}`} className="text-sm">
                        {index + 1}ª tentativa
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="number"
                            inputMode="decimal"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2"
                            value={value}
                            onChange={(event) => updateAttemptInput(muscle.key, index, event.target.value)}
                            placeholder={unit}
                          />
                          {currentInputs.length > PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts ? (
                            <button
                              type="button"
                              onClick={() => removeAttemptInput(muscle.key, index)}
                              className="rounded-xl p-2 text-slate-500 transition hover:text-red-600"
                              aria-label="Remover tentativa"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </label>
                    ))}
                  </div>

                  <div
                    className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
                      !muscle.completed
                        ? 'border-amber-200 bg-amber-50 text-amber-800'
                        : muscle.reproducible
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                  >
                    {!muscle.completed ? (
                      <>
                        Recomenda-se registrar ao menos {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts} medidas antes de avaliar a
                        reprodutibilidade. Você ainda pode salvar este registro parcial.
                      </>
                    ) : muscle.reproducible ? (
                      <>
                        Os 2 maiores valores diferiram {muscle.topTwoDiffPercent?.toFixed(1)}%. Critério de reprodutibilidade atendido.
                      </>
                    ) : (
                      <>
                        Os 2 maiores valores diferiram {muscle.topTwoDiffPercent?.toFixed(1)}%. Considere repetir o músculo até ficar abaixo de{' '}
                        {PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.reproducibilityThresholdPercent}% quando clinicamente possível.
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Interpretação parcial</div>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {summary.lowestPercentPredicted != null && summary.lowestMuscleLabel
            ? `Menor relação com o predito em ${summary.lowestMuscleLabel} (${summary.lowestPercentPredicted.toFixed(
                1,
              )}%).`
            : 'As equações do suplemento são exibidas automaticamente quando sexo, idade e BMI necessários estão disponíveis.'}
        </p>
      </div>

      <label className="block text-sm">
        Observações (opcional)
        <textarea
          className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Ex.: variação do teste escolhida para peitoral ou deltoide, dor, fadiga, interrupções."
        />
      </label>

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
          onClick={onCancel}
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={handleFinish}
          disabled={!canFinish}
          className="inline-flex items-center gap-2 rounded-full bg-primary-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          Salvar avaliação
        </button>
      </div>
    </div>
  );
}
